import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Users, Globe, TrendingUp, DollarSign, Target,
  BarChart3, MapPin, Percent, Clock, CheckCircle, AlertTriangle,
  Rocket, Eye, ShoppingCart, Zap, RefreshCw, ChevronRight,
  PieChart, Activity
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Banderas por país
const countryFlags = {
  'US': '🇺🇸', 'UK': '🇬🇧', 'DE': '🇩🇪', 'FR': '🇫🇷', 'ES': '🇪🇸',
  'IT': '🇮🇹', 'CA': '🇨🇦', 'AU': '🇦🇺', 'MX': '🇲🇽', 'CO': '🇨🇴',
  'AR': '🇦🇷', 'BR': '🇧🇷', 'JP': '🇯🇵', 'KR': '🇰🇷', 'IN': '🇮🇳'
};

// Idiomas por país
const countryLanguages = {
  'US': { code: 'en', name: 'Inglés (US)' },
  'UK': { code: 'en-gb', name: 'Inglés (UK)' },
  'DE': { code: 'de', name: 'Alemán' },
  'FR': { code: 'fr', name: 'Francés' },
  'ES': { code: 'es', name: 'Español' },
  'IT': { code: 'it', name: 'Italiano' },
  'CA': { code: 'en', name: 'Inglés/Francés' },
  'AU': { code: 'en', name: 'Inglés (AU)' },
  'MX': { code: 'es-mx', name: 'Español (MX)' },
  'CO': { code: 'es-co', name: 'Español (CO)' },
  'AR': { code: 'es-ar', name: 'Español (AR)' },
  'BR': { code: 'pt-br', name: 'Portugués (BR)' },
  'JP': { code: 'ja', name: 'Japonés' },
  'KR': { code: 'ko', name: 'Coreano' },
  'IN': { code: 'en-in', name: 'Inglés (IN)' }
};

// Componente de tarjeta de micro-comunidad
const CommunityCard = ({ community, index, onLaunchCampaign }) => {
  const countryCode = community.location?.country_code || 'US';
  const flag = countryFlags[countryCode] || '🌍';
  const language = countryLanguages[countryCode] || { code: 'en', name: 'Inglés' };
  
  const roiPotential = community.roi_potential || 0;
  const roiColor = roiPotential >= 70 ? 'emerald' : roiPotential >= 50 ? 'amber' : 'red';
  
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-zinc-700 transition-all">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="text-4xl">{flag}</div>
          <div>
            <h3 className="font-bold text-lg">{community.name}</h3>
            <p className="text-sm text-zinc-500">
              {community.location?.region ? `${community.location.region}, ` : ''}
              {community.location?.country}
            </p>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full bg-${roiColor}-500/20 text-${roiColor}-400 font-bold`}>
          ROI {roiPotential.toFixed(0)}
        </div>
      </div>
      
      {/* Grid de métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-zinc-800/50 rounded-xl p-3">
          <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1">
            <Users className="w-3 h-3" />
            Audiencia
          </div>
          <div className="text-lg font-bold text-blue-400">
            {(community.metrics?.audience_size || 0).toLocaleString()}
          </div>
        </div>
        
        <div className="bg-zinc-800/50 rounded-xl p-3">
          <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1">
            <TrendingUp className="w-3 h-3" />
            Demanda
          </div>
          <div className="text-lg font-bold text-emerald-400">
            {community.metrics?.demand_score || 0}/100
          </div>
        </div>
        
        <div className="bg-zinc-800/50 rounded-xl p-3">
          <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1">
            <Activity className="w-3 h-3" />
            Crecimiento
          </div>
          <div className="text-lg font-bold text-purple-400">
            +{community.metrics?.demand_growth_30d || 0}%
          </div>
        </div>
        
        <div className="bg-zinc-800/50 rounded-xl p-3">
          <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1">
            <DollarSign className="w-3 h-3" />
            CPM
          </div>
          <div className="text-lg font-bold text-amber-400">
            ${community.advertising?.estimated_cpm || 0}
          </div>
        </div>
      </div>
      
      {/* Intereses */}
      <div className="mb-4">
        <p className="text-xs text-zinc-500 mb-2">Intereses principales:</p>
        <div className="flex flex-wrap gap-2">
          {(community.interests?.primary || []).map((interest, i) => (
            <span key={i} className="text-xs px-2 py-1 bg-zinc-800 rounded-full text-zinc-300">
              {interest}
            </span>
          ))}
        </div>
      </div>
      
      {/* Idioma y acción */}
      <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Globe className="w-4 h-4" />
          <span>{language.name}</span>
        </div>
        
        <button
          onClick={() => onLaunchCampaign(community)}
          className="px-4 py-2 bg-emerald-500 text-black font-medium rounded-xl hover:bg-emerald-400 transition-all text-sm flex items-center gap-2"
        >
          <Rocket className="w-4 h-4" />
          Lanzar Campaña
        </button>
      </div>
      
      {/* Barra de confianza */}
      <div className="mt-4 pt-4 border-t border-zinc-800">
        <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
          <span>Confianza de datos</span>
          <span>{community.confidence?.score || 0}%</span>
        </div>
        <div className="w-full bg-zinc-800 rounded-full h-1.5">
          <div 
            className="bg-emerald-500 h-1.5 rounded-full transition-all"
            style={{ width: `${community.confidence?.score || 0}%` }}
          />
        </div>
        <div className="flex gap-2 mt-2">
          {(community.confidence?.data_sources || []).map((source, i) => (
            <span key={i} className="text-xs px-2 py-0.5 bg-zinc-800 rounded text-zinc-500">
              {source.replace('_', ' ')}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function OpportunityDetail() {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingStore, setGeneratingStore] = useState(false);

  useEffect(() => {
    loadOpportunity();
  }, [opportunityId]);

  const loadOpportunity = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cauchera/opportunities/${opportunityId}`);
      const data = await res.json();
      if (data.success) {
        setOpportunity(data.data);
      }
    } catch (error) {
      console.error("Error loading opportunity:", error);
    }
    setLoading(false);
  };

  const generateStore = async () => {
    setGeneratingStore(true);
    try {
      const res = await fetch(`${API_URL}/api/cauchera/stores/generate-from-opportunity?opportunity_id=${opportunityId}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        await loadOpportunity();
        alert(`Tienda creada: ${data.data.full_url}`);
      }
    } catch (error) {
      console.error("Error generating store:", error);
    }
    setGeneratingStore(false);
  };

  const launchCampaign = (community) => {
    alert(`Lanzar campaña para ${community.name} en ${community.location?.country}\nIdioma: ${countryLanguages[community.location?.country_code]?.name || 'Inglés'}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-zinc-400">Oportunidad no encontrada</p>
      </div>
    );
  }

  const product = opportunity.product || {};
  const validation = opportunity.validation || {};
  const financials = opportunity.financials || {};
  const communities = opportunity.target_communities || [];
  
  // Calcular totales de comunidades
  const totalAudience = communities.reduce((acc, c) => acc + (c.metrics?.audience_size || 0), 0);
  const avgDemand = communities.length > 0 
    ? communities.reduce((acc, c) => acc + (c.metrics?.demand_score || 0), 0) / communities.length 
    : 0;
  const avgROI = communities.length > 0
    ? communities.reduce((acc, c) => acc + (c.roi_potential || 0), 0) / communities.length
    : 0;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/cauchera/dashboard')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-bold text-xl">{product.name}</h1>
                <p className="text-sm text-zinc-500">Detalle de Oportunidad</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {opportunity.store_url ? (
                <a 
                  href={opportunity.store_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 border border-zinc-700 rounded-xl hover:bg-zinc-800 transition-all flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Ver Tienda
                </a>
              ) : opportunity.status === 'approved' && (
                <button
                  onClick={generateStore}
                  disabled={generatingStore}
                  className="px-4 py-2 bg-emerald-500 text-black font-medium rounded-xl hover:bg-emerald-400 transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  {generatingStore ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4" />
                  )}
                  {generatingStore ? 'Generando...' : 'Crear Tienda'}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Métricas Principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-xs text-zinc-500 mb-1">Trend Score</p>
            <p className="text-2xl font-bold text-emerald-400">{Math.round(product.trend_score || 0)}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-xs text-zinc-500 mb-1">Crecimiento 30d</p>
            <p className="text-2xl font-bold text-blue-400">+{product.demand_growth_30d || 0}%</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-xs text-zinc-500 mb-1">Vol. Búsquedas</p>
            <p className="text-2xl font-bold text-purple-400">{(product.search_volume || 0).toLocaleString()}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-xs text-zinc-500 mb-1">Score General</p>
            <p className="text-2xl font-bold text-emerald-400">{validation.overall_score || 0}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-xs text-zinc-500 mb-1">ROI Proyectado</p>
            <p className="text-2xl font-bold text-amber-400">{Math.round(financials.projected_roi_percent || 0)}%</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-xs text-zinc-500 mb-1">Riesgo</p>
            <p className={`text-2xl font-bold ${
              validation.risk_level === 'LOW' ? 'text-emerald-400' :
              validation.risk_level === 'MEDIUM' ? 'text-amber-400' : 'text-red-400'
            }`}>{validation.risk_level || 'N/A'}</p>
          </div>
        </div>

        {/* Resumen de Micro-Comunidades */}
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-400" />
              Micro-Comunidades Detectadas
            </h2>
            <span className="text-sm text-zinc-400">{communities.length} comunidades en {new Set(communities.map(c => c.location?.country_code)).size} países</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-4xl font-bold text-blue-400">{totalAudience.toLocaleString()}</p>
              <p className="text-sm text-zinc-500">Audiencia Total</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-emerald-400">{avgDemand.toFixed(0)}</p>
              <p className="text-sm text-zinc-500">Demanda Promedio</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-purple-400">{avgROI.toFixed(0)}</p>
              <p className="text-sm text-zinc-500">ROI Potencial Prom.</p>
            </div>
          </div>
        </div>

        {/* Lista de Micro-Comunidades */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Detalle por Comunidad</h2>
          
          {communities.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-12 text-center">
              <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">No hay micro-comunidades detectadas</p>
              <p className="text-sm text-zinc-600">Valida la oportunidad para identificar comunidades</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {communities.map((community, index) => (
                <CommunityCard 
                  key={community.id || index}
                  community={community}
                  index={index}
                  onLaunchCampaign={launchCampaign}
                />
              ))}
            </div>
          )}
        </div>

        {/* Financials */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            Proyección Financiera
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-zinc-500 mb-1">Costo Producto</p>
              <p className="text-lg font-bold">${financials.product_cost?.toFixed(2) || 0}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 mb-1">Precio Venta</p>
              <p className="text-lg font-bold text-emerald-400">${financials.recommended_price?.toFixed(2) || 0}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 mb-1">Margen Bruto</p>
              <p className="text-lg font-bold text-blue-400">{financials.gross_margin_percent?.toFixed(1) || 0}%</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 mb-1">Break-even</p>
              <p className="text-lg font-bold text-amber-400">{financials.break_even_units || 0} unids</p>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-zinc-800">
            <p className="text-sm text-zinc-500 mb-3">Escenarios de ROI</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-center">
                <p className="text-xs text-red-400 mb-1">Pesimista</p>
                <p className="text-xl font-bold text-red-400">{Math.round(financials.scenario_pessimistic_roi || 0)}%</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center">
                <p className="text-xs text-emerald-400 mb-1">Base</p>
                <p className="text-xl font-bold text-emerald-400">{Math.round(financials.scenario_base_roi || 0)}%</p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-center">
                <p className="text-xs text-blue-400 mb-1">Optimista</p>
                <p className="text-xl font-bold text-blue-400">{Math.round(financials.scenario_optimistic_roi || 0)}%</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
